import type { UploadQuotaResponse } from "@/api/client";

/**
 * Upload-quota helpers shared by every upload surface (`/riders/upload`,
 * the dashboard hero) so the Free-tier gate, the batch cap and the error
 * matching cannot drift between them (v1.77.0).
 *
 * Server contract (`enforce_upload_quota`, `GET /riders/upload-quota`):
 * - `tier` is the EFFECTIVE tier (own subscription or inherited via an org),
 *   so a Free user on an Explorer org is not gated here.
 * - Free is the only tier with a hard monthly cap (10, no overage). Paid
 *   tiers pass the server count check and are metered instead, so they are
 *   never clamped or gated client-side. Enterprise and admins report
 *   `limit: null` (unlimited); admins also bypass the count.
 * - The quota 403 `detail` starts with `Monthly upload limit reached`.
 */

export type UploadQuotaLike =
  | Pick<UploadQuotaResponse, "tier" | "limit" | "remaining">
  | null
  | undefined;

/** Per-batch staging cap; admins get the wide cap (they bypass the quota). */
export const DEFAULT_BATCH_SIZE = 10;
export const ADMIN_BATCH_SIZE = 80;

/** Uploads in flight at once — bounded so a batch cannot exhaust the API's small DB pool. */
export const DEFAULT_UPLOAD_CONCURRENCY = 3;

export const QUOTA_ERROR_NEEDLE = "Monthly upload limit reached";
export const QUOTA_ITEM_ERROR = "Monthly limit reached";
export const QUOTA_TOAST = "Monthly upload limit reached. Upgrade your plan to keep uploading.";
export const QUOTA_GATE_TOAST = "Monthly limit reached — upgrade to keep uploading";

/** True when an API error message is the server's monthly-quota 403. */
export function isQuotaError(msg: string | null | undefined): boolean {
  return typeof msg === "string" && msg.includes(QUOTA_ERROR_NEEDLE);
}

/**
 * True when the quota is a hard monthly cap the client must respect —
 * i.e. the effective tier is Free and the caller is not an admin.
 */
export function isHardCapQuota(
  quota: UploadQuotaLike,
  isAdmin: boolean,
): quota is NonNullable<UploadQuotaLike> {
  return !isAdmin && quota != null && quota.tier === "free" && quota.limit != null;
}

/** The Free allowance for this month is used up: gate the surface, offer Explorer. */
export function isFreeQuotaExhausted(quota: UploadQuotaLike, isAdmin: boolean): boolean {
  return isHardCapQuota(quota, isAdmin) && (quota.remaining ?? 0) <= 0;
}

/**
 * How many files a batch may stage.
 *
 * Only a hard-capped quota (Free) clamps to what is left this month, so a
 * batch never half-fails server-side. Overage tiers keep the plain cap even
 * at `remaining <= 0` (the server lets them through and meters). When Free
 * is exhausted the surface is gated by `isFreeQuotaExhausted`, so the cap
 * returned there is the base one rather than 0 (no "0 / 0 files" display).
 */
export function computeBatchCap(
  quota: UploadQuotaLike,
  isAdmin: boolean,
  base: number = isAdmin ? ADMIN_BATCH_SIZE : DEFAULT_BATCH_SIZE,
): number {
  if (!isHardCapQuota(quota, isAdmin)) return base;
  const remaining = quota.remaining ?? 0;
  if (remaining <= 0) return base;
  return Math.min(base, remaining);
}

/**
 * Uploads in flight for a batch. The server's quota check is count-then-insert
 * with no lock, so on a hard cap parallel uploads could overshoot it; Free
 * uploads one at a time, every other tier keeps the default concurrency.
 */
export function uploadConcurrency(quota: UploadQuotaLike, isAdmin: boolean): number {
  return isHardCapQuota(quota, isAdmin) ? 1 : DEFAULT_UPLOAD_CONCURRENCY;
}
